import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Tooltip,
  TextField,
  Stack,
  Typography
} from '@mui/material';
import { APIHOST } from '../../common';
import { useAuth, getToken } from '../auth/AuthContext';
import InfoIcon from '@mui/icons-material/Info';

interface DisplayCreditCardProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
}

export default function DisplayCreditCard({ open, onClose, orderId }: DisplayCreditCardProps) {
  const { currentUser } = useAuth();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [accessToken2, setAccessToken2] = useState<string | null>(null);
  const [creditCardZipCode, setCreditCardZipCode] = useState<string | null>(null);
  const [locator, setLocator] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cvvLimitReached, setCvvLimitReached] = useState(false);
  const [cvvUsagesRemaining, setCvvUsagesRemaining] = useState<number | null>(null);
  const [externalTransactionId, setExternalTransactionId] = useState('');
  const [internalTransactionId, setInternalTransactionId] = useState('');
  const [transactionIdMessage, setTransactionIdMessage] = useState<string | null>(null);
  const [savingTransactionIds, setSavingTransactionIds] = useState(false);

  const roleType = currentUser?.role?.name || '';
  const showInternalTransactionId = roleType.toLowerCase() === 'internal';

  useEffect(() => {
   
      if (!orderId) return;
      if (!open) return;

      generateAccessToken();
      console.log('Generating access token for order:', orderId);
  }, [orderId, open]);

  const generateAccessToken = async () => {
    try {
      setLoading(true);
      setError(null);
      setCvvLimitReached(false);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Check CVV retention policy
      const policyRes = await fetch(`${APIHOST}/api/orders/${orderId}/cvvRetentionPolicy`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!policyRes.ok) {
        throw new Error('Failed to fetch CVV retention policy');
      }
      const res = await policyRes.json();
      const policy = res?.cvvRetention;
      console.log('CVV Retention Policy:', policy);
      // Assume policy returns { quota: number, CvvDestinationUsage: number }
      const quota = policy.quota ?? 0;
      const usage = policy.usage ?? quota; // If usage is not provided, assume quota is used up to be safe
      const userRole = currentUser?.role?.name;


      // Calculate usages remaining
      let usagesRemaining = quota - usage;
      if (userRole !== 'Internal') usagesRemaining -= 5;
      usagesRemaining = Math.max(-1, usagesRemaining);
      setCvvUsagesRemaining(usagesRemaining);

      if (usagesRemaining < 0) {
        setCvvLimitReached(true);
        setAccessToken2(null);
      } else {
        setCvvLimitReached(false);
      }

      if (usagesRemaining < 0) {
        return;
      }
      
      // First token request
      const response1 = await fetch(`${APIHOST}/api/orders/${orderId}/generateAccessToken`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response1.ok) {
        throw new Error('Failed to generate first access token');
      }

      const data1 = await response1.json();
      setAccessToken(data1.accessToken);
      setLocator(data1.locator);
      setCreditCardZipCode(data1.creditCardZipCode);

      // Only generate 2nd token if allowed
      if (!cvvLimitReached && usagesRemaining >= 0) {
        const response2 = await fetch(`${APIHOST}/api/orders/${orderId}/generateAccessToken`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response2.ok) {
          throw new Error('Failed to generate second access token');
        }

        const data2 = await response2.json();
        setAccessToken2(data2.accessToken);
      } else {
        setAccessToken2(null);
      }
    } catch (err) {
      console.error('Error generating access token:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate access token');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAccessToken(null);
    setAccessToken2(null);
    setError(null);
    setTransactionIdMessage(null);
    onClose();
  };

  const handleTransactionIdSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTransactionIdMessage(null);

    const token = getToken();
    if (!token) {
      setError('No authentication token found');
      return;
    }

    try {
      setSavingTransactionIds(true);
      setError(null);

      const isInternalUser = currentUser?.role?.name === 'Internal';
      const endpoint = isInternalUser
        ? `${APIHOST}/api/orders/${orderId}/processPaymentCard`
        : `${APIHOST}/api/hotel-admin/orders/${orderId}/processPaymentCard`;
      const payload = isInternalUser
        ? { internalTransactionId, externalTransactionId }
        : { externalTransactionId };

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to save transaction ID values');
      }

      setTransactionIdMessage('Transaction ID values saved successfully.');
    } catch (err) {
      console.error('Error saving transaction IDs:', err);
      setError(err instanceof Error ? err.message : 'Failed to save transaction ID values');
    } finally {
      setSavingTransactionIds(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px'}
      }}
    >
      <DialogTitle>Credit Card Information</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {accessToken && !loading && (
          <Box sx={{ width: '100%', height: '350px' }}>
            <iframe
              src={`https://service.pcibooking.net/api/payments/paycard/${locator}/display?accessToken=${accessToken}${accessToken2 ? `&accessToken2=${accessToken2}` : ''}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: "#FFFFFF" 
              }}
              title="Credit Card Display"
            />
          </Box>
        )}
        {cvvUsagesRemaining !== null && (
          <Box p={2} sx={{ fontWeight: 500 }}>
            {!cvvLimitReached && <Alert severity={cvvUsagesRemaining < 3 ? "warning" : "info"} sx={{ mb: 2 }}>
              Credit Card display usages remaining for this order: {Math.max(cvvUsagesRemaining - 1, 0)}
            </Alert>}
            {cvvLimitReached && <Alert severity={"error"} sx={{ mb: 2 }}>
              No credit card display usages remaining for this card. {currentUser?.role?.name !== 'Internal' && <span>Please contact customer service to retrieve card information for this order.</span>}
            </Alert>}
            {currentUser?.role?.name !== 'Internal' && (
              <Typography variant="body2" color="textSecondary">
                Credit card display usages are limited to prevent unauthorized access. Hotel administrators are limited to 5 views per order.
              </Typography>
            )}
            {currentUser?.role?.name === 'Internal' && (
              <Tooltip title={<span>Credit card display usages are limited to prevent unauthorized access. Hotel users have a lower limit than internal users. They will be blocked from viewing the credit card after 5 views, while internal users have a higher limit. If you need to view the credit card after the limit is reached, please contact customer service.<br/><br/>After all credit card display usages are used up, the credit card will be blocked from display in the iframe, but the rest of the credit card info will still be visible. This is not reverseable, so please use views wisely.</span>}>
                <InfoIcon sx={{ ml: 1, verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </Box>
        )}
        <Box p={2}>Zip: {creditCardZipCode}</Box>
        <Box p={2} component="form" onSubmit={handleTransactionIdSubmit}>
          <Stack spacing={2}>
            {showInternalTransactionId && (
              <TextField
                label="Internal Transaction ID"
                value={internalTransactionId}
                onChange={(event) => setInternalTransactionId(event.target.value)}
                fullWidth
                helperText="For internal use only. Visible to CWHS employees. Not shared with hotels."
              />
            )}
            <TextField
              label="Hotel's Transaction ID"
              value={externalTransactionId}
              onChange={(event) => setExternalTransactionId(event.target.value)}
              fullWidth
              helperText="This is the hotel's transaction ID for processing this card. This allows hotels to track their own transaction IDs in relation to this order."
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={savingTransactionIds || (!showInternalTransactionId && !externalTransactionId) || (showInternalTransactionId && !internalTransactionId && !externalTransactionId)}
              >
                {savingTransactionIds ? 'Saving...' : 'Save'}
              </Button>
            </Box>
            {transactionIdMessage && (
              <Alert severity="success">{transactionIdMessage}</Alert>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}